import React, { useMemo, useState } from "react";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import useSupportCaseOptions from "../../hooks/useSupportCaseOptions";
import { apiCreateSupportCase, supportCaseErrorMessage } from "../../api/supportCaseApi";
import {
  SUBJECT_MAX,
  DESCRIPTION_MAX,
  validateCaseDraft,
  parseEvidenceUrls,
  readCase,
  orderContextForCase,
  buildCasePayload,
} from "../../utils/supportCaseVocabulary";

/**
 * ReportIssueDialog: the ONE form that creates a support case in this client.
 *
 * Whatever the entry point is (the order detail screen, or the customer's own
 * case list), it opens this. That is deliberate: the limits, the category list
 * and the evidence parsing all have exactly one implementation, so a second
 * entry point cannot ship a subtly different form.
 *
 * `order` IS OPTIONAL. Passing one prefills the case with that order and its
 * vendor; passing nothing gives the general door, for an account problem, a
 * payment that vanished or a fault in the app, none of which belong to an
 * order. orderContextForCase returns an empty context for a missing order and
 * buildCasePayload then omits both ids, which is exactly what the server wants:
 * a case with neither is valid and is routed to staff. There is no second form
 * for that case, and there must never be one.
 *
 * The category list comes from GET /api/support-cases/meta/options through
 * useSupportCaseOptions, never from a list typed in here.
 *
 * Validation mirrors the server's limits (subject and description required,
 * 140 and 4000 characters) so a customer hears about a too long description
 * while they are still looking at it. The server remains the gate.
 *
 * Follows the SubmitModal pattern in components/Reviews/Reviews.jsx: a plain
 * backdrop plus card, clicks inside stopped from closing it.
 */
const ReportIssueDialog = ({ open, onClose, order, onCreated }) => {
  const { categories } = useSupportCaseOptions();

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [evidenceText, setEvidenceText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  /* The order this case is about. An order carries its vendor in more than one
     place, so that lives in the shared module rather than here. */
  const context = useMemo(() => orderContextForCase(order), [order]);

  const reset = () => {
    setSubject("");
    setCategory("");
    setDescription("");
    setEvidenceText("");
    setErrors({});
  };

  const close = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async () => {
    const draft = { subject, description, category };
    const { valid, errors: bag } = validateCaseDraft(draft);
    const { evidence, rejected } = parseEvidenceUrls(evidenceText);
    const nextErrors = { ...bag };
    if (rejected.length) {
      nextErrors.evidence =
        "Each line has to be a full link starting with http or https. Check: " +
        rejected.slice(0, 3).join(", ");
    }
    setErrors(nextErrors);
    if (!valid || rejected.length) return;

    setSubmitting(true);
    try {
      const data = await apiCreateSupportCase(
        buildCasePayload({ subject, description, category, context, evidence })
      );
      const created = readCase(data);
      toast.success(
        created?.caseNumber
          ? `Thank you. Your case ${created.caseNumber} is open.`
          : "Thank you. Your case is open."
      );
      reset();
      onClose?.();
      onCreated?.(created);
    } catch (error) {
      toast.error(supportCaseErrorMessage(error, "We could not open your case. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Backdrop onClick={close}>
      <Card onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Report an issue">
        <Header>
          <h3>Report an issue</h3>
          <IconBtn type="button" onClick={close} aria-label="Close">
            <CloseIcon />
          </IconBtn>
        </Header>

        {context.orderNumber ? (
          <Context>
            About order <b>{context.orderNumber}</b>
          </Context>
        ) : (
          <Context>
            Not about one order. Tell us what happened and the Lusuku team will
            pick it up.
          </Context>
        )}

        <Field>
          <Label htmlFor="support-case-subject">Subject</Label>
          <Input
            id="support-case-subject"
            value={subject}
            maxLength={SUBJECT_MAX}
            $invalid={Boolean(errors.subject)}
            placeholder="One line on what went wrong"
            onChange={(e) => setSubject(e.target.value)}
          />
          <Foot>
            {errors.subject ? <Err>{errors.subject}</Err> : <span />}
            <Count>
              {subject.length} of {SUBJECT_MAX}
            </Count>
          </Foot>
        </Field>

        <Field>
          <Label htmlFor="support-case-category">What is this about</Label>
          <Select
            id="support-case-category"
            value={category}
            $invalid={Boolean(errors.category)}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">Choose one</option>
            {categories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {errors.category ? <Err>{errors.category}</Err> : null}
        </Field>

        <Field>
          <Label htmlFor="support-case-description">What happened</Label>
          <TextArea
            id="support-case-description"
            value={description}
            maxLength={DESCRIPTION_MAX}
            $invalid={Boolean(errors.description)}
            rows={6}
            placeholder="Dates, items, amounts, anything that helps us see it the way you did."
            onChange={(e) => setDescription(e.target.value)}
          />
          <Foot>
            {errors.description ? <Err>{errors.description}</Err> : <span />}
            <Count>
              {description.length} of {DESCRIPTION_MAX}
            </Count>
          </Foot>
        </Field>

        <Field>
          <Label htmlFor="support-case-evidence">Links to photos or files (optional)</Label>
          <TextArea
            id="support-case-evidence"
            value={evidenceText}
            $invalid={Boolean(errors.evidence)}
            rows={3}
            placeholder={"One link per line, for example https://example.com/photo.jpg"}
            onChange={(e) => setEvidenceText(e.target.value)}
          />
          {errors.evidence ? <Err>{errors.evidence}</Err> : null}
          <Hint>You can add more links later from the case itself.</Hint>
        </Field>

        <Actions>
          <Ghost type="button" onClick={close} disabled={submitting}>
            Cancel
          </Ghost>
          <Primary type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Sending..." : "Send report"}
          </Primary>
        </Actions>
      </Card>
    </Backdrop>
  );
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 22px 22px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.24);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;

  h3 {
    margin: 0;
    font-size: 19px;
    font-weight: 800;
    color: #1f2937;
  }
`;

const IconBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  padding: 2px;
  line-height: 1;
`;

const Context = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 14px;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 6px;
`;

const field = (p) => `
  width: 100%;
  border-radius: 9px;
  border: 1px solid ${p.$invalid ? "#dc2626" : "#d8dbde"};
  padding: 10px 12px;
  font-size: 14px;
  color: #1f2937;
  background: #ffffff;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${p.$invalid ? "#dc2626" : "#5d8f24"};
  }
`;

const Input = styled.input`
  ${field}
`;

const Select = styled.select`
  ${field}
`;

const TextArea = styled.textarea`
  ${field}
  resize: vertical;
  line-height: 1.5;
`;

const Foot = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-top: 5px;
`;

const Err = styled.div`
  font-size: 12.5px;
  color: #dc2626;
  font-weight: 600;
`;

const Count = styled.div`
  font-size: 11.5px;
  color: #9ca3af;
  flex: none;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 5px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const Primary = styled.button`
  border: none;
  border-radius: 9px;
  padding: 11px 20px;
  cursor: pointer;
  background: #5d8f24;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;

  &:disabled {
    background: #b6c8a3;
    cursor: default;
  }
`;

const Ghost = styled.button`
  border: 1px solid #d8dbde;
  border-radius: 9px;
  padding: 11px 18px;
  cursor: pointer;
  background: #ffffff;
  color: #374151;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    color: #9ca3af;
    cursor: default;
  }
`;

export default ReportIssueDialog;
